import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Tabs, Tab } from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import FormSpinner from 'components/FormSpinner';
import { RMSButton } from '../../shared/components/buttons';
import { logOut } from 'reducers/UserReducer';
import EvidencePortalMyEvidenceTable from '../components/portal/EvidencePortalMyEvidenceTable';
import EvidencePortalCheckoutRequestsTable from '../components/portal/EvidencePortalCheckoutRequestsTable';
import EvidencePortalCorrectionsTable from '../components/portal/EvidencePortalCorrectionsTable';
import EvidencePortalItemDetailDialog from '../components/portal/EvidencePortalItemDetailDialog';
import NotificationPreferencesSection from '../components/preferences/NotificationPreferencesSection';
import usePortalDashboardSummary from '../hooks/usePortalDashboardSummary';
import useMyCorrectionRequests from '../hooks/useMyCorrectionRequests';
import useMyCheckoutRequests from '../hooks/useMyCheckoutRequests';
import usePortalMyEvidenceRecords from '../hooks/usePortalMyEvidenceRecords';
import useEvidencePortalItemDetail from '../hooks/useEvidencePortalItemDetail';
import { EVIDENCE_PORTAL_TAB_KEYS } from '../constants/evidencePortalConstants';
import { EVIDENCE_ROUTE_PATHS } from '../constants/evidenceRoutePaths';
import clsx from 'clsx';

const EvidencePortalPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [tab, setTab] = useState(EVIDENCE_PORTAL_TAB_KEYS.MY_EVIDENCE);

  const currentUserName = useSelector(
    (state) => state.user?.userData?.user?.FullName || state.user?.userData?.user?.Username
  );
  const agencyId = useSelector((state) => state.user?.userAgency);

  const { summary, summaryLoading, personId } = usePortalDashboardSummary();
  const { corrections, correctionsLoading, refetchCorrections } = useMyCorrectionRequests();
  const { checkoutRequests, checkoutRequestsLoading } = useMyCheckoutRequests();
  const evidenceRecords = usePortalMyEvidenceRecords(personId);
  const {
    open: detailOpen,
    data: detailData,
    matchedRules,
    loading: detailLoading,
    openItem,
    closeItem,
  } = useEvidencePortalItemDetail();

  const isLoading =
    summaryLoading || correctionsLoading || checkoutRequestsLoading || detailLoading;

  const handleLogout = () => {
    dispatch(logOut());
    history.replace(EVIDENCE_ROUTE_PATHS.portalLogin);
  };

  return (
    <div className="evidence-portal">
      {isLoading && <FormSpinner />}
      <div className="evidence-portal__header">
        <div>
          <span className="evidence-portal__title">Officer Evidence Portal</span>
          <span className="evidence-portal__subtitle">
            Signed in as <strong>{currentUserName || 'Officer'}</strong>
          </span>
        </div>
        <RMSButton
          label="Logout"
          variant="outlined"
          size="small"
          prefixIcon={<ExitToAppIcon fontSize="small" />}
          onClick={handleLogout}
          className="evidence-portal__logout-btn"
        />
      </div>
      <Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        indicatorColor="primary"
        textColor="primary"
        className="evidence-portal__tabs">
        <Tab
          value={EVIDENCE_PORTAL_TAB_KEYS.MY_EVIDENCE}
          label={`My Evidence (${evidenceRecords.total})`}
          className={clsx('evidence-portal__tab', {
            'evidence-portal__tab--active': tab === EVIDENCE_PORTAL_TAB_KEYS.MY_EVIDENCE,
          })}
        />

        <Tab
          value={EVIDENCE_PORTAL_TAB_KEYS.CHECKOUT}
          label={`Checkout Requests (${checkoutRequests.length})`}
          className={clsx('evidence-portal__tab', {
            'evidence-portal__tab--active': tab === EVIDENCE_PORTAL_TAB_KEYS.CHECKOUT,
          })}
        />

        <Tab
          value={EVIDENCE_PORTAL_TAB_KEYS.CORRECTIONS}
          label={`Corrections (${corrections.length})`}
          className={clsx('evidence-portal__tab', {
            'evidence-portal__tab--active': tab === EVIDENCE_PORTAL_TAB_KEYS.CORRECTIONS,
          })}
        />

        <Tab
          value={EVIDENCE_PORTAL_TAB_KEYS.SETTINGS}
          label="Settings"
          className={clsx('evidence-portal__tab', {
            'evidence-portal__tab--active': tab === EVIDENCE_PORTAL_TAB_KEYS.SETTINGS,
          })}
        />
      </Tabs>
      <div className="evidence-portal__body">
        {tab === EVIDENCE_PORTAL_TAB_KEYS.MY_EVIDENCE && (
          <EvidencePortalMyEvidenceTable
            agencyId={agencyId}
            summary={summary}
            evidenceRecords={evidenceRecords}
            onOpenEvidence={openItem}
          />
        )}

        {tab === EVIDENCE_PORTAL_TAB_KEYS.CHECKOUT && (
          <div className="evidence-portal__section">
            <EvidencePortalCheckoutRequestsTable onOpenEvidence={openItem} />
          </div>
        )}

        {tab === EVIDENCE_PORTAL_TAB_KEYS.CORRECTIONS && (
          <div className="evidence-portal__section">
            <EvidencePortalCorrectionsTable
              corrections={corrections}
              onOpenEvidence={openItem}
              refetchCorrections={refetchCorrections}
            />
          </div>
        )}

        {tab === EVIDENCE_PORTAL_TAB_KEYS.SETTINGS && (
          <div className="evidence-preferences">
            <div className="evidence-preferences__body">
              <NotificationPreferencesSection />
            </div>
          </div>
        )}
      </div>
      <EvidencePortalItemDetailDialog
        open={detailOpen}
        loading={detailLoading}
        data={detailData}
        matchedRules={matchedRules}
        onClose={closeItem}
      />
    </div>
  );
};

export default EvidencePortalPage;
